'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import apiFetch from '@/lib/fetch';
import { YouTubeEmbed } from '@next/third-parties/google';
import { useCurrentDeviceContext } from '@/context/DeviceContext';

interface AdsProps {
  placement: string;
  serviceSlug?: string;
  districtSlug?: string;
  blogSlug?: string;
  initialData?: AdData[];
  deviceType?: 'mobile' | 'desktop';
}

interface AdData {
  id: string;
  title: string;
  media_type: 'image' | 'video';
  desktop_url: string;
  mobile_url: string;
  link?: string;
}

export default function Ads({ initialData, placement, serviceSlug, districtSlug, blogSlug, deviceType }: AdsProps) {
  // Only the creative matching the current device is rendered, instead of
  // printing both variants and hiding one of them with CSS.
  const { device } = useCurrentDeviceContext();
  const isMobile = device === 'mobile';
  const [ads, setAds] = useState<AdData[]>(initialData || []);
  const [loading, setLoading] = useState(initialData?.length == 0);

  const getImageUrl = (id: string, url: string) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `/ads/${id}/${url}`;
  };

  const extractYouTubeId = (url: string): string | null => {
    const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };


  useEffect(() => {
    if (Array.isArray(initialData) && initialData.length > 0) return;

    const fetchAds = async () => {
      try {
        setLoading(true);

        const queryParams = new URLSearchParams();
        queryParams.append('placement', placement);

        if (serviceSlug) queryParams.append('serviceSlug', serviceSlug);
        if (districtSlug) queryParams.append('districtSlug', districtSlug);
        if (blogSlug) queryParams.append('blogSlug', blogSlug);

        const response = await apiFetch({
          route: `ads?${queryParams.toString()}`
        });

        if (response && Array.isArray(response)) {
          setAds(response);
        }
      } catch (error) {
        console.error('Error fetching ads:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
  }, [initialData, placement, serviceSlug, districtSlug, blogSlug]);

  // A placement that targets the other device is skipped entirely
  if (deviceType && deviceType !== device) {
    return null;
  }

  if (loading) {
    return <div className="ads-loading">Yükleniyor...</div>;
  }

  if (!ads.length) {
    return null;
  }

  return (
    <div className="ads-container flex flex-col items-center gap-4 w-full">
      {ads.map((ad) => (
        <React.Fragment key={ad.id}>
          {ad.media_type === 'image' ? (
            <div className="ad-image-container w-full">
              {!isMobile && ad.desktop_url && (
                ad.link ? (
                  <a href={ad.link} target="_blank" rel="noopener noreferrer">
                    <Image
                      src={getImageUrl(ad.id, ad.desktop_url)}
                      alt={ad?.title}
                      width={1200}
                      height={200}
                      className="w-full h-auto"
                      priority
                    />
                  </a>
                ) : (
                  <Image
                    src={getImageUrl(ad.id, ad.desktop_url)}
                    alt={ad?.title}
                    width={1200}
                    height={200}
                    className="w-full h-auto"
                    priority
                  />
                )
              )}
              {isMobile && ad.mobile_url && (
                ad.link ? (
                  <a href={ad.link} target="_blank" rel="noopener noreferrer">
                    <Image
                      src={getImageUrl(ad.id, ad.mobile_url)}
                      alt={ad?.title}
                      width={320}
                      height={200}
                      className="mx-auto"
                      priority
                    />
                  </a>
                ) : (
                  <Image
                    src={getImageUrl(ad.id, ad.mobile_url)}
                    alt={ad?.title}
                    width={320}
                    height={200}
                    className="mx-auto"
                    priority
                  />
                )
              )}
            </div>
          ) : (
            <div className="ad-video-container">
              {!isMobile && ad.desktop_url && (() => {
                const videoId = extractYouTubeId(ad.desktop_url);
                const content = (
                  <div className="max-w-[600px] w-full aspect-video">
                    <YouTubeEmbed
                      videoid={videoId}
                      width={600}
                      playlabel={ad?.title}
                    />
                  </div>
                );
                return ad.link ? (
                  <a href={ad.link} target="_blank" rel="noopener noreferrer">{content}</a>
                ) : content;
              })()}

              {isMobile && ad.mobile_url && (() => {
                const videoId = extractYouTubeId(ad.mobile_url);
                const content = (
                  <div className="w-full h-96 aspect-video">
                    <YouTubeEmbed
                      style="width: 100%; height: 100%; max-height: 384px;"
                      videoid={videoId}
                      playlabel={ad?.title}
                    />
                  </div>
                );
                return ad.link ? (
                  <a href={ad.link} target="_blank" rel="noopener noreferrer">{content}</a>
                ) : content;
              })()}
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}